import type { Collection, DocumentPreview, FilterOptions } from "./types";

export const collections: Collection[] = [
  {
    id: "municipal-finance",
    name: "Municipal finance",
    description: "MFMA circulars, funding, reporting, and oversight.",
    count: 42,
    accent: "green",
  },
  {
    id: "supply-chain",
    name: "Supply chain",
    description: "Procurement rules, SCM circulars, and bid guidance.",
    count: 28,
    accent: "gold",
  },
  {
    id: "budget-planning",
    name: "Budget planning",
    description: "MTREF circulars, budget templates, and adjustments.",
    count: 35,
    accent: "blue",
  },
  {
    id: "governance",
    name: "Governance",
    description: "MPAC, annual reports, councils, and audit oversight.",
    count: 31,
    accent: "red",
  },
];

export const documentPreviews: DocumentPreview[] = [
  {
    id: "mfma-circular-55",
    title: "MFMA Circular No. 55: Municipal Budget Circular",
    collectionId: "budget-planning",
    collectionName: "Budget planning",
    type: "Circular",
    year: 2012,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/Circulars/Circular%2055/2012.10.12%20-%20MFMA%20Circular%20No.%2055_Final.pdf",
    fileName: "2012.10.12 - MFMA Circular No. 55_Final.pdf",
    pages: 19,
    fileSize: "477 KB",
    updatedAt: "12 Oct 2012",
    relevance: 96,
    matchStrength: "Strong match",
    tags: ["MTREF", "budget process", "municipal budgets"],
    excerpt:
      "Guidance for municipalities preparing medium-term revenue and expenditure framework budgets, including schedules and supporting documentation.",
    sectionMatches: ["Budget preparation", "MTREF submissions", "Supporting schedules"],
    summary:
      "A budget preparation circular focused on municipal MTREF planning, budget credibility, and required supporting information.",
    keyFacts: [
      "Applies to municipal budget preparation cycles.",
      "Emphasizes credible revenue and expenditure assumptions.",
      "Useful for finance officials reviewing MTREF source material.",
    ],
    excerpts: [
      {
        heading: "Budget credibility",
        text: "Municipalities are expected to ensure that budget assumptions are realistic and supported by credible revenue projections.",
        page: 4,
      },
      {
        heading: "Supporting schedules",
        text: "The circular points readers to schedules and supporting tables that accompany municipal budget submissions.",
        page: 9,
      },
    ],
    relatedDocuments: ["mfma-circular-99", "budget-template"],
  },
  {
    id: "mfma-circular-82",
    title: "MFMA Circular No. 82: Cost Containment Measures",
    collectionId: "municipal-finance",
    collectionName: "Municipal finance",
    type: "Circular",
    year: 2016,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/Circulars/Documents/MFMA%20Circular%20No%2082%20-%20Cost%20Containment%20Measures.pdf",
    fileName: "MFMA Circular No 82 - Cost Containment Measures.pdf",
    pages: 15,
    fileSize: "1.2 MB",
    updatedAt: "30 Mar 2016",
    relevance: 93,
    matchStrength: "Strong match",
    tags: ["cost containment", "expenditure", "controls"],
    excerpt:
      "Sets expectations for municipalities and entities to contain costs, strengthen internal controls, and redirect spending toward service delivery.",
    sectionMatches: ["Consultants", "Travel", "Advertising", "Catering"],
    summary:
      "A high-value source for identifying expenditure controls and cost containment expectations in municipal operations.",
    keyFacts: [
      "Covers common discretionary spending categories.",
      "Frames cost containment as a service-delivery discipline.",
      "Useful when comparing local policies against Treasury guidance.",
    ],
    excerpts: [
      {
        heading: "Spending controls",
        text: "The circular identifies spending areas where municipalities should apply stricter controls and oversight.",
        page: 3,
      },
      {
        heading: "Policy alignment",
        text: "Municipalities should align internal policies with cost containment principles and monitor compliance.",
        page: 11,
      },
    ],
    relatedDocuments: ["cost-containment-regulations", "mfma-circular-97"],
  },
  {
    id: "scm-regulations",
    title: "Municipal Supply Chain Management Regulations",
    collectionId: "supply-chain",
    collectionName: "Supply chain",
    type: "Gazette",
    year: 2005,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/RegulationsAndGazettes/Municipal%20SCM%20Regulations.pdf",
    fileName: "Municipal SCM Regulations.pdf",
    pages: 44,
    fileSize: "720 KB",
    updatedAt: "30 May 2005",
    relevance: 89,
    matchStrength: "Good match",
    tags: ["SCM", "procurement", "bid committees"],
    excerpt:
      "Regulations establishing municipal supply chain management systems, procurement processes, bid committee roles, and oversight requirements.",
    sectionMatches: ["Demand management", "Acquisition management", "Bid adjudication"],
    summary:
      "Primary regulatory source for municipal procurement process requirements and committee structures.",
    keyFacts: [
      "Defines core municipal SCM system elements.",
      "Includes bid specification, evaluation, and adjudication requirements.",
      "Useful as a baseline source before reading later circulars.",
    ],
    excerpts: [
      {
        heading: "SCM system",
        text: "Each municipality must have and implement a supply chain management policy that gives effect to the regulatory framework.",
        page: 5,
      },
      {
        heading: "Bid committees",
        text: "Committee roles are separated to support fairness, accountability, and proper evaluation of bids.",
        page: 28,
      },
    ],
    relatedDocuments: ["mfma-circular-53", "mfma-circular-96"],
  },
  {
    id: "mpac-guide",
    title: "Guide and Toolkit for Municipal Public Accounts Committees",
    collectionId: "governance",
    collectionName: "Governance",
    type: "Guideline",
    year: 2012,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/Guidelines/Guide%20and%20Toolkit%20for%20Municipal%20Public%20Accounts%20Committees.pdf",
    fileName: "Guide and Toolkit for Municipal Public Accounts Committees.pdf",
    pages: 84,
    fileSize: "3.1 MB",
    updatedAt: "Aug 2012",
    relevance: 86,
    matchStrength: "Good match",
    tags: ["MPAC", "oversight", "annual reports"],
    excerpt:
      "Explains MPAC oversight responsibilities and practical tools for reviewing annual reports, audit outcomes, and municipal performance.",
    sectionMatches: ["Oversight cycle", "Annual report review", "Committee workplans"],
    summary:
      "A practical governance source for council oversight, MPAC operations, and annual report review workflows.",
    keyFacts: [
      "Supports councillors and committee members.",
      "Connects oversight work to annual reporting obligations.",
      "Includes practical templates and review prompts.",
    ],
    excerpts: [
      {
        heading: "Oversight role",
        text: "The guide frames MPAC work as structured oversight over financial and performance accountability.",
        page: 12,
      },
      {
        heading: "Annual report review",
        text: "Committees should consider audit findings, service delivery information, and financial outcomes together.",
        page: 36,
      },
    ],
    relatedDocuments: ["annual-report-template", "mfma-circular-32"],
  },
  {
    id: "cost-containment-regulations",
    title: "Municipal Cost Containment Regulations, 2019",
    collectionId: "municipal-finance",
    collectionName: "Municipal finance",
    type: "Gazette",
    year: 2019,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/RegulationsAndGazettes/Municipal%20Cost%20Containment%20Regulations%202019.pdf",
    fileName: "Municipal Cost Containment Regulations 2019.pdf",
    pages: 18,
    fileSize: "846 KB",
    updatedAt: "7 Jun 2019",
    relevance: 84,
    matchStrength: "Good match",
    tags: ["cost containment", "regulations", "expenditure"],
    excerpt:
      "Regulations prescribing cost containment measures for municipalities and municipal entities across selected expenditure categories.",
    sectionMatches: ["Consultants", "Domestic travel", "Credit cards", "Sponsorships"],
    summary:
      "A regulatory source that strengthens and formalizes cost containment expectations for local government.",
    keyFacts: [
      "Creates binding cost containment requirements.",
      "Complements earlier Treasury circular guidance.",
      "Useful for compliance review and policy drafting.",
    ],
    excerpts: [
      {
        heading: "Containment measures",
        text: "The regulations specify categories of spending where municipalities must apply cost containment measures.",
        page: 4,
      },
    ],
    relatedDocuments: ["mfma-circular-82", "mfma-circular-97"],
  },
  {
    id: "annual-report-template",
    title: "Annual Report Template for Municipalities",
    collectionId: "governance",
    collectionName: "Governance",
    type: "Template",
    year: 2012,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/Guidelines/Annual%20Report%20Template%20July%202012.pdf",
    fileName: "Annual Report Template July 2012.pdf",
    pages: 67,
    fileSize: "2.4 MB",
    updatedAt: "Jul 2012",
    relevance: 77,
    matchStrength: "Related",
    tags: ["annual reports", "template", "performance"],
    excerpt:
      "Template structure for municipal annual reports, including governance, service delivery, financial performance, and audit information.",
    sectionMatches: ["Report structure", "Performance information", "Financial reporting"],
    summary:
      "A source template for understanding how annual report information is organized and reviewed.",
    keyFacts: [
      "Provides a repeatable report structure.",
      "Supports consistent annual report preparation.",
      "Useful alongside MPAC oversight material.",
    ],
    excerpts: [
      {
        heading: "Report structure",
        text: "The template organizes annual report content into governance, performance, human resources, and financial sections.",
        page: 6,
      },
    ],
    relatedDocuments: ["mpac-guide", "mfma-circular-32"],
  },
  {
    id: "budget-template",
    title: "Batho Pele City Annual Budget Template",
    collectionId: "budget-planning",
    collectionName: "Budget planning",
    type: "Template",
    year: 2011,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/Guidelines/Annual%20Budget%20Batho%20Pele%20City%20Template.pdf",
    fileName: "Annual Budget Batho Pele City Template.pdf",
    pages: 55,
    fileSize: "1.9 MB",
    updatedAt: "2011",
    relevance: 74,
    matchStrength: "Related",
    tags: ["budget process", "template", "MTREF"],
    excerpt:
      "Illustrative municipal budget template showing budget narrative, schedules, financial tables, and supporting assumptions.",
    sectionMatches: ["Budget narrative", "Schedules", "Financial tables"],
    summary:
      "A useful reference for reviewing the expected structure of a municipal budget pack.",
    keyFacts: [
      "Illustrates how budget schedules can be presented.",
      "Pairs naturally with MTREF circulars.",
      "Useful for source review rather than legal authority.",
    ],
    excerpts: [
      {
        heading: "Budget narrative",
        text: "The template demonstrates a structured budget narrative supported by tables and financial schedules.",
        page: 8,
      },
    ],
    relatedDocuments: ["mfma-circular-55", "mfma-circular-99"],
  },
  {
    id: "s71-webinar",
    title: "Section 71 Reporting Webinar Pack",
    collectionId: "municipal-finance",
    collectionName: "Municipal finance",
    type: "Webinar",
    year: 2025,
    department: "National Treasury",
    sourceUrl:
      "https://mfma.treasury.gov.za/Media_Releases/s71-webinar-pack.pdf",
    fileName: "20250415 S71 Webinar - PPT_NTDS.pdf",
    pages: 32,
    fileSize: "1.4 MB",
    updatedAt: "15 Apr 2025",
    relevance: 70,
    matchStrength: "Related",
    tags: ["Section 71", "reporting", "webinar"],
    excerpt:
      "Presentation material for municipal Section 71 reporting processes, deadlines, data quality, and common submission issues.",
    sectionMatches: ["Submission process", "Data quality", "Reporting deadlines"],
    summary:
      "A training source for officials reviewing reporting process material and Section 71 data quality guidance.",
    keyFacts: [
      "Training-oriented rather than primary legislation.",
      "Useful for process and quality-control source discovery.",
      "Connects reporting deadlines to data submission practices.",
    ],
    excerpts: [
      {
        heading: "Reporting process",
        text: "The webinar material highlights common submission checks and data quality controls for Section 71 reporting.",
        page: 14,
      },
    ],
    relatedDocuments: ["mfma-circular-55", "annual-report-template"],
  },
];

export const filterOptions: FilterOptions = {
  documentTypes: ["Circular", "Guideline", "Gazette", "Legislation", "Template", "Webinar"],
  years: [2025, 2019, 2016, 2012, 2011, 2005],
  tags: [
    "MTREF",
    "SCM",
    "annual reports",
    "budget process",
    "cost containment",
    "governance",
    "oversight",
    "procurement",
    "reporting",
  ],
};
